import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import iconSpinVideo from '../assets/branding/icon_spin.mp4';
import xTwitterIcon from '../assets/icons/x_twitter.svg';
import rinkImage from '../assets/images/rink.jpg';
import FeedGrid from '../components/feed/FeedGrid/FeedGrid';
import GeneratorComponent from '../components/Generator';
import GoogleSignInButton from '../components/GoogleSignInButton';
import AnimationText from '../components/TextCarousel';
import TopExpertsSection from '../components/TopExpertsSection';
import TopPicksSection from '../components/TopPicksSection';
import { ROUTES } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, isAdmin, isExpert } = useAuth();

  React.useEffect(() => {
    if (isAuthenticated && isAdmin) {
      navigate('/hub');
    }
  }, [isAuthenticated, isAdmin, navigate]);

  return (
    <div className="home">
      <Helmet>
        <title>PhilipSnat | Expert Sports Betting Tips & Predictions</title>
        <meta name="description" content="Follow expert sports tipsters, track their betting picks, and discover the best predictions for NHL, KHL, and more. Find verified experts or share your own picks." />
        <meta property="og:title" content="PhilipSnat | Expert Sports Betting Tips & Predictions" />
        <meta property="og:description" content="Follow expert sports tipsters, track their betting picks, and discover the best predictions for NHL, KHL, and more." />
        <meta property="og:url" content="https://philipsnat.com/" />
        <link rel="canonical" href="https://philipsnat.com/" />
      </Helmet>
      <section className="home__hero">
        <div className="home__hero-content">
          <a
            className="home__hero-eyebrow"
            href="https://x.com/Philip_snat"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={xTwitterIcon} alt="" className="home__hero-x-icon" aria-hidden="true" />
            <span>@Philip_snat</span>
          </a>
          <h1 className="home__hero-headline">
            <span className="home__hero-headline-static">{t.home.title1}</span>
            <br />
            <span className="home__hero-headline-for">{t.home.title2}&nbsp;</span>
            <span className="home__hero-carousel">
              <AnimationText texts={t.home.textCarousel} />
            </span>
          </h1>
          <p className="home__hero-sub">
            {t.home.heroSub}
          </p>
          <div className="home__hero-nav-links">
            <button
              className="home__hero-nav-btn"
              onClick={() => navigate(isExpert ? '/expert-panel/recommendations/add' : ROUTES.EXPERTS_SEARCH)}
            >
              {isExpert ? t.home.addRecommendation : t.home.searchExperts}
            </button>
            <button
              className="home__hero-nav-btn"
              onClick={() => navigate(isExpert ? ROUTES.EXPERTS_SEARCH : ROUTES.BECOME_EXPERT)}
            >
              {isExpert ? t.home.searchExperts : t.home.becomeExpert}
            </button>
          </div>
          <div className="home__hero-actions">
            <GoogleSignInButton className="home__google-button" />
          </div>
        </div>
        <div className="home__hero-visual">
          <div className="home__hero-video-wrap">
            <video
              className="home__hero-video"
              src={iconSpinVideo}
              autoPlay
              loop
              muted
              playsInline
            />
          </div>
        </div>
      </section>

      <section className="home__feed">
        <FeedGrid />
      </section>

      <section className="home__top-sections">
        <TopExpertsSection limit={5} showRangeSwitch />
        <TopPicksSection limit={5} showRangeSwitch showSubtitle={false} />
      </section>

      <div className="home__banner">
        <img src={rinkImage} alt={t.home.sportsArenaAlt} className="home__banner-image" />
        <div className="home__banner-overlay" />
      </div>

      <section className="home__demo">
        <GeneratorComponent isDemo={true} maxEvents={4} defaultEvents={4} />
      </section>
    </div>
  );
};

export default Home;
